import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useMediaQuery } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getDesignTokens } from './linearTheme';
import { safeGetItem } from '../lib/storage';

const ThemeContext = createContext({
  mode: 'dark',
  toggleColorMode: () => { },
});

export const useThemeContext = () => useContext(ThemeContext);

// Also read by the pre-paint script in public/index.html, which sets data-theme before
// React mounts. Keep the two in step — they must resolve to the same mode or the first
// frame disagrees with the app.
export const THEME_KEY = 'themeMode';

export const ThemeContextProvider = ({ children }) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  // A manual choice wins over the system preference and survives navigation/reload.
  // Without this the toggle was reset to the system value on every mount.
  const [mode, setMode] = useState(
    () => safeGetItem(THEME_KEY) || (prefersDarkMode ? 'dark' : 'light'),
  );

  useEffect(() => {
    document.documentElement.dataset.theme = mode; // keep Tailwind dark:/vars in sync
  }, [mode]);

  // Follow the system preference only while the user has made no explicit choice.
  useEffect(() => {
    if (safeGetItem(THEME_KEY)) return;
    setMode(prefersDarkMode ? 'dark' : 'light');
  }, [prefersDarkMode]);

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prevMode) => {
          const next = prevMode === 'light' ? 'dark' : 'light';
          try {
            localStorage.setItem(THEME_KEY, next);
          } catch (error) {
            // Storage unavailable — the toggle still works for this session, matching how
            // AppSidebar treats its collapse state.
          }
          return next;
        });
      },
    }),
    [mode],
  );

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  return (
    <ThemeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
